import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Paper,
  Button,
  Alert,
  Snackbar,
  Backdrop,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import { LoaderWrapper } from "../../components/index";

// Import API services
import {
  getApplicationForm,
  saveApplicationForm,
  submitApplication,
  getLabById,
  getApplicationId,
} from "../../services/api";

// Import all DO sections
import SubmittedApplicationSection from "./application/SubmittedApplication";
import LabHistoryPage from "./application/LabHistoryPage";
import ViewApplicationPage from "./application/ViewApplicationPage";
import AppDashboard from "./application/AppDashboard";
import MOMCompository from "./application/MOMCompository";
import DoAllotment from "./application/DoAllotment";
import AssessorAllotment from "./application/AssessorAllotment";
import ParameterAllotment from "./application/ParameterAllotment";
import AuditTrail from "./application/AuditTrail";
import UnfreezeApplication from "./application/UnfreezeApplication";
import CollectionCenter from "./application/CollectionCenter";
import AccountsDetails from "./application/AccountsDetails";
import Form217Preview from "./AssessmentReport/Form217Report";
import NCMainReport from "./AssessmentReport/NCMainReport";
import NCApplicationFinding from "./AssessmentReport/NCApplicationFinding";
import DoNCReply from "./AssessmentReport/DoNCReply";
import Form11 from "./application/Form11";
import Form11_new from "./application/Form11_new";
import Form72 from "./application/Form72";
import Form73Report from "./application/Form73";
import FormTestWitness from "./AssessmentReport/FormTestwitness";
import ACProcess from "./AC_Process/ACProcess";
import DecisionProcess from "./AC_Process/DecisionProcess";
import LabDetails from "./AC_Process/LabDetails";
import Advisory from "./AC_Process/Advisory";
import TADA from "./application/TADA";
import EditScopeCertificate from "./AC_Process/EditScopeCertificate";
import Form73Calibration from "./application/Form73Calibration";
import OutstandingLedger from "./OutstandingLedger";
import AssessmentTrackingLog from "./application/AssessmentTrackingLog";
import TestWitnessReport from "./application/TestWitnessReport";
import DoAssessmentControl from "./application/DoAssessmentControl";
import AssessorSearchManagement from "../../components/AssessorSearch/AssessorSearchManagement";


const DRAWER_WIDTH = 320;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64; // Height of the existing ApplicationHeader

const DoLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentSection, setCurrentSection] = useState(
    "submitted-applications"
  );
  const [formData, setFormData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labData, setLabData] = useState(null);
  const [appId, setAppId] = useState(null);
  const [showDeclaration, setShowDeclaration] = useState(false);

  // Section configuration
  const sections = useMemo(
    () => [
      {
        id: "submitted-applications",
        title: "Submitted Applications",
        component: SubmittedApplicationSection,
        path: "/main/do/dashboard",
      },
      {
        id: "lab-history",
        title: "Lab History",
        component: LabHistoryPage,
        path: "/main/do/applications/history",
      },
      {
        id: "view-application",
        title: "View Application",
        component: ViewApplicationPage,
        path: "/main/do/applications/view",
      },
      {
        id: "app-dashboard",
        title: "App Dashboard",
        component: AppDashboard,
        path: "/main/do/applications/appdashboard",
      },
      {
        id: "mom-compository",
        title: "MOM Compository",
        component: MOMCompository,
        path: "/main/do/momcompository",
      },
      {
        id: "do-allotment",
        title: "Group Applicaitons",
        component: DoAllotment,
        path: "/main/do/doallotment",
      },
      {
        id: "assessor-allotment",
        title: "Assessor Allotment",
        component: AssessorAllotment,
        path: "/main/do/assessor-allotment",
      },
      {
        id: "parameter-allotment",
        title: "Parameter Allotment",
        component: ParameterAllotment,
        path: "/main/do/parameter-allotment",
      },
      {
        id: "audit-trail",
        title: "Audit Trail",
        component: AuditTrail,
        path: "/main/do/audit-trail",
      },
      {
        id: "edit-scope-certificate",
        title: "Edit Scope Certificate",
        component: EditScopeCertificate,
        path: "/main/do/edit-scope-certificate",
      },
      {
        id: "unfreeze-application",
        title: "Unfreeze Application",
        component: UnfreezeApplication,
        path: "/main/do/unfreezeApplication",
      },
      {
        id: "collection-center",
        title: "Collection Center",
        component: CollectionCenter,
        path: "/main/do/collection-center",
      },
      {
        id: "Form217-Preview",
        title: "Form217 Preview",
        component: Form217Preview,
        path: "/main/do/form217-preview",
      },
      {
        id: "FormTestWitness",
        title: "FormTestWitness",
        component: FormTestWitness,
        path: "/main/do/FormTestWitness",
      },
      {
        id: "NC Report",
        title: "NC Report",
        component: NCMainReport,
        path: "/main/do/nc-report",
      },
      {
        id: "NC Reply",
        title: "NC Reply",
        component: DoNCReply,
        path: "/main/do/nc-reply",
      },
      {
        id: "NC Application",
        title: "NC Application",
        component: NCApplicationFinding,
        path: "/main/do/nc-application",
      },
      {
        id: "accounts-details",
        title: "Accounts Details",
        component: AccountsDetails,
        path: "/main/do/accounts-details",
      },
      {
        id: "form11",
        title: "Form 11",
        component: Form11,
        path: "/main/do/form11",
      },
      {
        id: "form11-new",
        title: "Form 11",
        component: Form11_new,
        path: "/main/do/form11-new",
      },
      {
        id: "form72",
        title: "Form 72",
        component: Form72,
        path: "/main/do/form72",
      },
      {
        id: "form73",
        title: "Form 73",
        component: Form73Report,
        path: "/main/do/form73",
      },
      {
        id: "form73-calibration",
        title: "Form 73",
        component: Form73Calibration,
        path: "/main/do/form73-calibration",
      },
      {
        id: "ac-process",
        title: "AC Process",
        component: ACProcess,
        path: "/main/do/ac-process",
      },
      {
        id: "decision-process",
        title: "Decision Process",
        component: DecisionProcess,
        path: "/main/do/decision-process",
      },
      {
        id: "lab-details",
        title: "Lab Details",
        component: LabDetails,
        path: "/main/do/lab-details",
      },
      {
        id: "advisory",
        title: "Advisory",
        component: Advisory,
        path: "/main/do/advisory",
      },
      {
        id: "TA-DA",
        title: "TA-DA",
        component: TADA,
        path: "/main/do/TADA",
      },
      {
        id: "outstanding-ledger",
        title: "Outstanding Amount & Ledger",
        component: OutstandingLedger,
        path: "/main/do/outstanding-ledger",
      },
      {
        id: "test-witness-report-id",
        title: "Test Witness Report",
        component: TestWitnessReport,
        path: "/main/do/testWitnessReport",
      },
      {
        id: "do-assessment-control-id",
        title: "Assessment Control",
        component: DoAssessmentControl,
        path: "/main/do/assessment-control",
      },
      {
        id: "do-assessment-tracking-log-id",
        title: "Assessment Tracking Log",
        component: AssessmentTrackingLog,
        path: "/main/do/assessment-tracking-log",
      },
      {
        id: "assessor-search",
        title: "Assessor Search",
        component: AssessorSearchManagement,
        path: "/main/assessor-search",
      },
    ],
    []
  );

  // Determine current section based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const section = sections.find((s) => s.path === currentPath);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormDataUpdate = async (sectionId, data) => {
    const updatedFormData = {
      ...formData,
      [sectionId]: {
        ...formData[sectionId],
        ...data,
      },
    };

    setFormData(updatedFormData);
  };

  const navigateToSection = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      navigate(section.path);
    }
  };

  const getCurrentSectionIndex = () => {
    return sections.findIndex((s) => s.id === currentSection);
  };

  const handlePreviousSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      navigateToSection(sections[currentIndex - 1].id);
    }
  };

  // Submit complete application
  const submitCompleteApplication = async () => {
    try {
      setLoading(true);

      if (!appId) {
        setError("No application ID found. Please refresh the page.");
        return;
      }

      const payload = {
        appId: appId,
        formData: formData,
        labTypeId: 3, // Medical lab type
        completedSections: completedSections,
      };

      await submitApplication(payload);

      // Show success message and redirect
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate("/main/dashboard"); // or wherever you want to redirect after submission
      }, 2000);
    } catch (error) {
      console.error("Error submitting application:", error);
      setError("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNextSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < sections.length - 1) {
      navigateToSection(sections[currentIndex + 1].id);
    } else {
      // Last section - submit application
      submitCompleteApplication();
    }
  };

  const renderCurrentSection = () => {
    console.log("🔍 DoLayout renderCurrentSection:", {
      currentSection,
      pathname: location.pathname,
      availableSections: sections.map((s) => ({ id: s.id, path: s.path })),
    });

    const section = sections.find((s) => s.id === currentSection);
    if (!section) {
      console.log("❌ Section not found for currentSection:", currentSection);
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">
            Section not found: {currentSection}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Current path: {location.pathname}
          </Typography>
        </Box>
      );
    }

    console.log("✅ Rendering section:", section.title, section.component.name);
    const SectionComponent = section.component;

    return (
      <SectionComponent
        data={formData[currentSection] || {}}
        onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
      />
    );
  };

  const currentSectionIndex = getCurrentSectionIndex();
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === sections.length - 1;

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      sx={{ display: "flex", height: "100vh", pt: `${APP_HEADER_HEIGHT}px` }}
    >
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid #e0e0e0",
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: "flex",
            flexDirection: "column",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: "hidden",
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          activeStep={currentSectionIndex}
          onSectionClick={(item) => navigate(item.path)}
          showDeclaration={showDeclaration}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : "100%" },
          ml: { lg: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: "relative",
        }}
      >
        {/* Toggle Sidebar Button for Desktop when Sidebar is Closed */}
        {isMobile && !sidebarOpen && (
          <Box
            sx={{
              position: "fixed",
              top: `${APP_HEADER_HEIGHT + 10}px`,
              left: 10,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                boxShadow: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: "100%" }}>
          {/* Loading Indicator */}
          {loading && (
            <LoaderWrapper
              loading={true}
              type="page"
              message="Loading application data..."
            />
          )}

          <Paper sx={{ minHeight: "calc(100vh - 280px)", mb: 3 }}>
            {renderCurrentSection()}
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Save Status Indicator */}
          {saveLoading && (
            <LoaderWrapper
              loading={true}
              type="inline"
              message="Saving form data..."
              size="small"
            />
          )}
        </Container>
      </Box>

      {/* Save Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Form data saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoLayout;
